"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  CHECK_GAMES,
  CHECK_TOTAL,
  DIMENSIONS,
  REWARD_PER_MILESTONE,
  REWARD_TOTAL,
  bandFor,
  type DimensionKey,
  type PlanKey,
} from "./content";
import { readLeadPrefill } from "./lead-prefill";
import { flushQueuedViews, identifyViewer, trackView } from "./track";
import { postCheckResults, type Spoc } from "./miles360";
import { resumeIndex, type View } from "./views";

const KEY = "caira_check_v1";

/** Traffic source, captured from the URL the person landed on. */
function utmFromUrl() {
  if (typeof window === "undefined") return {};
  const out: Record<string, string> = {};
  new URLSearchParams(window.location.search).forEach((v, k) => {
    if (/^utm_/i.test(k) || k === "src") out[k] = v;
  });
  return out;
}

/**
 * The page the person filled the enquiry form on. Every form that sends people
 * here appends it as `?source_page_url=`; failing that, the referrer is the best
 * we have (for a visit from another site it holds only that site's origin).
 * "" means unknown, and an existing saved value is then kept.
 */
function sourcePageFromLanding() {
  if (typeof window === "undefined") return "";
  const fromForm = new URLSearchParams(window.location.search).get("source_page_url");
  if (fromForm) return fromForm;
  try {
    const ref = document.referrer;
    // Coming from another thank-you URL (a reload, a forward) says nothing new.
    if (!ref || new URL(ref).pathname.includes("/thank-you")) return "";
    return ref;
  } catch {
    return "";
  }
}

export type CheckState = {
  i: number;
  answers: Record<string, string | number | boolean>;
  rounds: Record<string, boolean>;
  points: Record<string, number>;
  reward: number;
  credited: Record<string, boolean>;
  /** Set once a purchase exists in Miles 360, so results carry the link. */
  purchaseUuid: string | null;
  /** Their own enrolment form, so a failed payment can be retried without
   *  creating a second purchase. */
  enrollmentUrl: string | null;
  /** The counsellor assigned to them, so every screen can name a real person. */
  spoc: Spoc | null;
  name: string;
  /** 10 digits, the key every drop-off event is posted under. */
  phone: string;
  code: string;
  scratched: boolean;
  track: PlanKey;
  deadline: number | null;
  /** Where they came from, sent with every Miles 360 record. See sourcePageFromLanding. */
  sourcePageUrl: string;
};

const initial: CheckState = {
  i: 0,
  answers: {},
  rounds: {},
  points: {},
  reward: 0,
  credited: {},
  purchaseUuid: null,
  enrollmentUrl: null,
  spoc: null,
  name: "",
  phone: "",
  code: "",
  scratched: false,
  track: "caira",
  deadline: null,
  sourcePageUrl: "",
};

/* eslint-disable no-unused-vars */
type Ctx = {
  state: CheckState;
  total: number;
  stepId: string;
  go: (i: number) => void;
  next: () => void;
  back: () => void;
  restart: () => void;
  setAnswer: (k: string, v: string | number | boolean) => void;
  recordRound: (k: string, correct: boolean) => void;
  recordPoints: (k: string, n: number) => void;
  award: (k: string) => number;
  roundsCorrect: number;
  roundsDone: number;
  dims: Record<DimensionKey, number>;
  scorePct: number;
  patch: (p: Partial<CheckState>) => void;
  /** Record arrival at a view. Called by the flow, once per view. */
  trackArrival: (viewId: string, name: string) => void;
  /** True once saved progress has been read back, so state.i is the real card. */
  hydrated: boolean;
};
/* eslint-enable no-unused-vars */

const CheckCtx = createContext<Ctx | null>(null);

export function CheckProvider({
  children,
  views,
}: {
  children: ReactNode;
  views: readonly View[];
}) {
  const [state, setState] = useState<CheckState>(initial);
  // `hydrated` is state, not a ref, so the persist effect below can only run on a render that
  // already holds the restored state. With a ref it fired in the same commit as the read and
  // wrote the pre-hydration state back, which wiped the player's progress on every refresh.
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        setState((s) => ({ ...s, ...initial, ...saved, i: resumeIndex(saved) }));
      }
    } catch {
      /* ignore */
    }
    // A fresh arrival from a form names its page; a reload or a return visit
    // without one keeps whatever was saved.
    const source = sourcePageFromLanding();
    if (source) setState((s) => ({ ...s, sourcePageUrl: source }));
    // Most people arrive from the lead form, which leaves their number behind.
    // Picking it up here means their very first screen is already tracked.
    try {
      const { phone } = readLeadPrefill();
      if (phone) {
        identifyViewer(phone);
        setState((s) => (s.phone ? s : { ...s, phone }));
      }
    } catch {
      /* ignore */
    }
    setHydrated(true);
    void flushQueuedViews();
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    try {
      // The view id goes with the index, so a change to the screen order can't resume them elsewhere.
      localStorage.setItem(KEY, JSON.stringify({ ...state, at: views[state.i]?.id }));
    } catch {
      /* ignore */
    }
  }, [state, hydrated, views]);

  const patch = useCallback((p: Partial<CheckState>) => {
    setState((s) => ({ ...s, ...p }));
  }, []);

  const go = useCallback(
    (i: number) => {
      setState((s) => ({ ...s, i: Math.max(0, Math.min(i, views.length - 1)) }));
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [views.length],
  );

  const next = useCallback(() => go(state.i + 1), [go, state.i]);

  // Called by the flow when a view mounts. The tracker dedupes per session, so
  // a re-render or a bounce back to an earlier screen posts nothing extra.
  const trackArrival = useCallback(
    (viewId: string, name: string) => {
      void trackView({ phone: state.phone, viewId, name, sourcePageUrl: state.sourcePageUrl });
    },
    [state.phone, state.sourcePageUrl],
  );

  const back = useCallback(() => go(state.i - 1), [go, state.i]);

  const restart = useCallback(() => {
    try {
      localStorage.removeItem(KEY);
    } catch {
      /* ignore */
    }
    setState(initial);
  }, []);

  const setAnswer = useCallback((k: string, v: string | number | boolean) => {
    setState((s) => ({ ...s, answers: { ...s.answers, [k]: v } }));
  }, []);

  const recordRound = useCallback((k: string, correct: boolean) => {
    setState((s) => (k in s.rounds ? s : { ...s, rounds: { ...s.rounds, [k]: correct } }));
  }, []);

  // Raw points a game scored, written once so a replayed card can't inflate the total.
  const recordPoints = useCallback((k: string, n: number) => {
    setState((s) => (k in s.points ? s : { ...s, points: { ...s.points, [k]: n } }));
  }, []);

  const award = useCallback((k: string) => {
    let value = 0;
    setState((s) => {
      if (s.credited[k]) {
        value = s.reward;
        return s;
      }
      const reward = Math.min(REWARD_TOTAL, s.reward + REWARD_PER_MILESTONE);
      value = reward;
      return { ...s, reward, credited: { ...s.credited, [k]: true } };
    });
    return value;
  }, []);

  const roundsCorrect = useMemo(
    () => Object.values(state.rounds).filter(Boolean).length,
    [state.rounds],
  );
  const roundsDone = Object.keys(state.rounds).length;

  // Mindset / Skillset / Toolset, summed from the games that feed each one.
  const dims = useMemo(() => {
    const out = Object.fromEntries(DIMENSIONS.map((d) => [d.k, 0])) as Record<DimensionKey, number>;
    CHECK_GAMES.forEach((g) => {
      out[g.dim] += Math.min(g.max, state.points[g.k] ?? 0);
    });
    return out;
  }, [state.points]);

  const scorePct = useMemo(() => {
    const earned = DIMENSIONS.reduce((n, d) => n + dims[d.k], 0);
    return Math.round((earned / CHECK_TOTAL) * 100);
  }, [dims]);

  // keep the saved lead in sync as they progress
  // Keep Miles 360 up to date as they progress. Idempotent on phone, so every
  // call simply replaces the previous state for that contact. Fire-and-forget:
  // a failure here must never interrupt someone mid-flow.
  useEffect(() => {
    if (!state.phone) return;
    void postCheckResults({
      phone: state.phone,
      score: scorePct,
      band: bandFor(scorePct)?.title ?? "",
      rounds_correct: roundsCorrect,
      mindset_score: dims.mindset,
      skillset_score: dims.skillset,
      toolset_score: dims.toolset,
      reward_earned: state.reward,
      track: state.track,
      completed: state.i >= views.length - 3, // the verdict screen onwards
      coupon_code: state.code || undefined,
      purchase_uuid: state.purchaseUuid,
      utm: utmFromUrl(),
      source_page_url: state.sourcePageUrl,
      answers: state.answers,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phone, state.sourcePageUrl, state.i, state.reward, state.track, state.code, state.purchaseUuid, roundsCorrect, scorePct, dims]);

  const value = useMemo<Ctx>(
    () => ({
      state,
      total: views.length,
      stepId: views[state.i]?.id ?? "",
      go,
      next,
      back,
      restart,
      setAnswer,
      recordRound,
      recordPoints,
      award,
      roundsCorrect,
      roundsDone,
      dims,
      scorePct,
      patch,
      trackArrival,
      hydrated,
    }),
    [
      state,
      views,
      go,
      next,
      back,
      restart,
      setAnswer,
      recordRound,
      recordPoints,
      award,
      roundsCorrect,
      roundsDone,
      dims,
      scorePct,
      patch,
      trackArrival,
      hydrated,
    ],
  );

  return <CheckCtx.Provider value={value}>{children}</CheckCtx.Provider>;
}

export function useCheck() {
  const ctx = useContext(CheckCtx);
  if (!ctx) throw new Error("useCheck must be used inside CheckProvider");
  return ctx;
}

